/**
 * Box Truck Loads → Loadboard. Shows the SYLECTUS_EMAIL feed (Sylectus posted-load emails) via
 * loads-ingest-service unified search, anywhere-to-anywhere. Bespoke table (reference columns incl.
 * a mock Match column) + a side drawer that expands into Load Details / Nearby Drivers / Draft Email
 * tabs. Reuses Load Hub's origin/destination/pickup/equipment filter inputs and email components.
 *
 * Three kinds of board share that table: the lane search tabs, and the two PINNED pills that run no lane
 * query — Driver Bids (what this company's drivers bid on) and My Biddings (what the signed-in dispatcher
 * has emailed a bid about). Exactly one is on screen; `pinnedBoard` says which, and `activeFeed` is the
 * pinned list behind it.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { RotateCcw, Search, Settings, Truck, X } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
    BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS,
    BOX_TRUCK_EQUIPMENT_LABELS,
    type BoxTruckCapacityLimits,
    CanonicalLoad,
    createDefaultBoxTruckCapacityLimits,
    defaultBoxTruckDatSubtypeCodes,
    sanitizeBoxTruckCapacityLimits,
    sanitizeBoxTruckDatSubtypeCodes,
    sanitizeBoxTruckMaxPostedAgeHours,
} from '@numeo/types-core'
import { normalizeNumeoIdQuery } from '@numeo/types'
import { useAppSelector } from 'hooks'
import { useAuth } from 'hooks/auth'
import { useLatestRef } from 'hooks/useLatestRef'
import { selectFeatureFlags } from 'store/authSlice'
import { LocationData } from 'features/load-hub/types/filters'
import { getToday } from 'features/load-hub/utils/date'
import {
    BEON_SOURCE,
    beonRequestable,
    boardSearchInstanceId,
    BoxTruckLoadType,
    BoxTruckSearchFilters,
    countOriginLanes,
    DAT_SOURCE,
    EMPTY_FILTERS,
    excludedSourcesFor,
    getDefaultPickupEnd,
    type RequestedSupplements,
} from '../hooks/requestMapping'
import { useBoxTruckDatEnabled } from '../hooks/useBoxTruckDatEnabled'
import { useBoxTruckBeonEnabled } from '../hooks/useBoxTruckBeonEnabled'
import { useBoxTruckBeonConnection } from '../hooks/useBoxTruckBeonConnection'
import { useGatedSourceToggle } from '../hooks/useGatedSourceToggle'
import { AihubAuthModal } from 'features/load-hub/components/AihubAuthModal'
import type { BoxTruckVerification } from '../services/boxTruckLoadsApi'
import { useBoxTruckLoads } from '../hooks/useBoxTruckLoads'
import { useBoxTruckBidFeed } from '../hooks/useBoxTruckBidFeed'
import { driverBidFeedSource, myBidFeedSource } from '../hooks/bidFeedSources'
import { useBoxTruckBoardView } from '../hooks/useBoxTruckBoardView'
import { useBoxTruckFilterPersistence } from '../hooks/useBoxTruckFilterPersistence'
import { useBoxTruckTabs } from '../hooks/useBoxTruckTabs'
import { useBoxTruckBrowserTabHandoff, useOpenBoxTruckBrowserTab } from '../hooks/useBoxTruckBrowserTabHandoff'
import { useBoxTruckDriverSelections } from '../hooks/useBoxTruckDriverSelections'
import { useBoxTruckViewPrefs } from '../hooks/useBoxTruckViewPrefs'
import { useReconnectReconcile } from '../hooks/useReconnectReconcile'
import { useLiveSearchRenewal } from '../hooks/useLiveSearchRenewal'
import { BoxTruckTabsBar } from '../components/BoxTruckTabsBar'
import { BoxTruckFilters } from '../components/BoxTruckFilters'
import { BoxTruckRefinementBar, type BoxTruckRefinements } from '../components/BoxTruckRefinementBar'
import { BoxTruckAdvancedFilters } from '../components/BoxTruckAdvancedFilters'
import { BoxTruckCapacityControls } from '../components/BoxTruckCapacityControls'
import { BOX_TRUCK_COLUMN_DEFS } from '../components/BoxTruckTable'
import { useBoxTruckColumns } from '../hooks/useBoxTruckColumns'
import { useBoxTruckBoardPreferences } from '../hooks/useBoxTruckBoardPreferences'
import { BoxTruckLoadDetailSettings } from '../components/BoxTruckLoadDetailSettings'
import { TableSettings } from '@/components/ds/TableSettings'
import { BoxTruckCompanyFilter } from '../components/BoxTruckCompanyFilter'
import { useClassifiedBrokers } from '@/hooks/useClassifiedBrokers'
import { buildBrokerListMatcher } from '../utils/brokerListMatcher'
import { BrokerType } from '@numeo/types-core'
import { BoxTruckSourceBar } from '../components/BoxTruckSourceBar'
import { BoxTruckLoadDrawer } from '../components/BoxTruckLoadDrawer'
import { SearchVehiclesDrawer } from '../components/SearchVehiclesDrawer'
import { BoxTruckBoard } from '../components/BoxTruckBoard'
import { BoxTruckBrowserTabGate } from '../components/BoxTruckBrowserTabGate'
import { withDriverEquipment } from '../utils/driverEquipment'
import { loadKey } from '../utils/loadFields'
import { omitBoard, omitBoards, resolveSelectedLoad, type SelectedLoadCache } from '../utils/selectedLoad'
import { boardStateKey, type BoxTruckPinnedBoard, DRIVER_BIDS_KEY, isPinnedBoardKey } from '../utils/pinnedBoard'
import {
    trackBoxTruckCapacityLimitChanged,
    trackBoxTruckDatSubtypesChanged,
    trackBoxTruckLoadOpened,
    trackBoxTruckPageViewed,
    trackBoxTruckSearchSubmitted,
    trackBoxTruckUiInteraction,
} from '../utils/analytics'
import { hasActiveBoxTruckSecondaryFilters, hasResettableBoxTruckSecondaryFilters } from '../utils/boxTruckFilterSummary'
import { clearAdvancedRefinements, defaultRefinementsForBoard, unhideSourceOnEveryBoard } from '../utils/boxTruckAdvancedFilterState'
import { useBoxTruckCompanyFilter } from '../hooks/useBoxTruckCompanyFilter'

/** Radius (miles) a city pick defaults to — mirrors the mobile board's DEFAULT_CITY_DEADHEAD. */
/** Stable empties for a pinned board's view — fresh literals per render would re-run the memoized view. */
const EMPTY_EQUIPMENT: string[] = []
/** Stable empty selection for a tab with no picked drivers — Nearby Drivers keys a fetch off this prop, so a
 *  fresh `[]` per render would refetch on every page render. */
const EMPTY_DRIVERS: BoxTruckVerification[] = []
const EMPTY_HIDDEN = new Set<string>()

const DEFAULT_CITY_RADIUS_MI = 150

export const BoxTruckLoadboardPage = () => {
    const { user, application } = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        handoff: { requested: browserHandoffRequested, isolatedWindow, importableWorkspace: importableHandoff, ready: handoffReady, notice: handoffNotice },
        switchGate: {
            waitingForAuth: handoffWaitingForAuth,
            needsCompanySwitch: handoffNeedsCompanySwitch,
            switching: handoffSwitching,
            error: handoffSwitchError,
        },
        retrySwitch: retryHandoffSwitch,
        openFresh: openFreshIsolatedSearch,
        dismissNotice: dismissHandoffNotice,
    } = useBoxTruckBrowserTabHandoff({ userId: user?.id, applicationId: application?.id })
    const {
        error: openBrowserTabError,
        openBrowserTab,
        dismissError: dismissOpenBrowserTabError,
    } = useOpenBoxTruckBrowserTab({
        userId: user?.id,
        applicationId: application?.id,
    })
    // A URL deep-link → a ready-to-run search: the Map "Find loads" button (a driver's shared location as a geo
    // origin + their equipment) or the state marker's "View table" (an exact-state origin). Present → open a
    // dedicated, already-searched tab for it (useBoxTruckTabs); absent → the normal localStorage-restored board.
    const deepLinkFilters = useMemo<BoxTruckSearchFilters | null>(() => {
        const equipmentParam = searchParams.get('equipment')?.trim()
        // Only seed a KNOWN box-truck label — free-text equipment ("Other") has no server class to filter on.
        const equipmentLabels = equipmentParam && BOX_TRUCK_EQUIPMENT_LABELS.includes(equipmentParam) ? [equipmentParam] : []

        const lat = Number(searchParams.get('originLat'))
        const lng = Number(searchParams.get('originLng'))
        const hasCoords = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)
        const stateRaw = searchParams.get('originState')?.trim().toUpperCase()
        const stateProv = stateRaw && /^[A-Z]{2}$/.test(stateRaw) ? stateRaw : undefined

        let origin: LocationData | null = null
        let originRadius: number | null = null
        if (hasCoords) {
            // Driver "Find loads": a geo-radius search around the driver's shared GPS point.
            origin = { city: searchParams.get('originCity')?.trim() || '', stateProv: stateProv ?? '', postalCode: '', latitude: lat, longitude: lng }
            originRadius = DEFAULT_CITY_RADIUS_MI
        } else if (stateProv) {
            // State "View table": an exact origin-state match.
            origin = { stateProv }
        }

        if (!origin && equipmentLabels.length === 0) return null
        return {
            ...EMPTY_FILTERS,
            origin,
            originOptions: origin ? [origin] : [],
            originRadius,
            equipmentLabels,
            startDate: getToday(),
            endDate: getDefaultPickupEnd(),
        }
    }, [searchParams])

    // Restored last-searched filters (localStorage) — seeds the first tab's draft when there's no deep-link.
    const { initialFilters: storedFilters, persistFilters } = useBoxTruckFilterPersistence(isolatedWindow)
    const initialFilters = importableHandoff?.tab.filters ?? deepLinkFilters ?? storedFilters

    // The deep-link tab is created ONCE from these params (useBoxTruckTabs' mount initializer). Strip the filter
    // params afterwards so reloading / re-navigating the same URL doesn't append a DUPLICATE searched tab each
    // time (they persist to localStorage). `selectLoad` is left in place for its own effect to consume.
    const deepLinkClearedRef = useRef(false)
    useEffect(() => {
        if (deepLinkClearedRef.current || !deepLinkFilters) return
        deepLinkClearedRef.current = true
        const next = new URLSearchParams(searchParams)
        for (const key of ['originLat', 'originLng', 'originCity', 'originState', 'equipment', 'driverName']) next.delete(key)
        setSearchParams(next, { replace: true })
    }, [deepLinkFilters, searchParams, setSearchParams])
    // Declared ahead of the feed hook because the request gate reads them; both are ASSIGNED further down,
    // once the Sources-bar state and the active tab exist. Safe in that order because neither is read during
    // render — only inside a request or a click handler, by which point the render has finished.
    // Two vocabularies, kept apart on purpose (see SEARCH_GATED_SOURCES): the chips the operator switched
    // off, and the request source names that removes from the search.
    const excludedSourcesRef = useRef<ReadonlySet<string>>(new Set())
    const activeTabSearchedRef = useRef(false)
    /** The ACTIVE tab's live-search identity. Deterministic off the tab id so a reload takes over the
     *  subscriptions that tab already holds instead of leaving them live until TTL, and per-tab so two tabs
     *  searching different lanes never replace each other's. Written below, once the tabs exist. */
    const liveSearchBoardIdRef = useRef<string | null>(null)
    /** The A/B arm of the search stream: `featureFlags.boxTruckStreamSearch` from /auth/verify, per user. Off keeps `search-unified`. */
    const viaSearchStreamRef = useLatestRef(useAppSelector(selectFeatureFlags).boxTruckStreamSearch === true)
    const {
        loads,
        loading,
        error,
        bids,
        operatorBids,
        annotations,
        emailThreads,
        threadsKnownFor,
        setAnnotation,
        search,
        clear,
        refresh: reloadFeed,
        loadMore,
        loadingMore,
        saveSnapshot,
        restoreSnapshot,
        dropSnapshot,
        releaseBoard,
        liveSearchLifetimes,
        forgetGatedSource,
        revealGatedSource,
    } = useBoxTruckLoads(user?.id, excludedSourcesRef, liveSearchBoardIdRef, viaSearchStreamRef)
    // Multi-search tabs (Load Hub model): opens on ONE unsearched tab (a "New search" state), so nothing is
    // fetched until Search. First tab seeds from the restored filters; each tab holds its own searched lane.
    const { tabs, activeTabId, activeTab, addTab, removeTab, closeAll, switchTab, commitFilters, reorderTabs, renameTab } = useBoxTruckTabs(
        initialFilters,
        deepLinkFilters,
        // Map "Find loads" passes the driver's name so the deep-link tab opens named after them.
        deepLinkFilters ? searchParams.get('driverName') : null,
        {
            requested: browserHandoffRequested,
            isolated: isolatedWindow,
            tab: importableHandoff?.tab,
            persist: handoffReady,
        }
    )

    // Read by the Sources toggle: switching a gated source back on re-runs the ACTIVE search, and there is no
    // active search to re-run on a tab still sitting on its "New search" state.
    activeTabSearchedRef.current = !!activeTab.searched
    liveSearchBoardIdRef.current = activeTabId ? boardSearchInstanceId(activeTabId) : null
    // Filter inputs (draft) seed from the restored active tab. `origin`/`destination` mirror the first chip
    // (radius + legacy); `originOptions`/`destinationOptions` are the multi-select source of truth.
    const initialDraftFilters = importableHandoff?.draftFilters ?? activeTab.filters
    const [origin, setOrigin] = useState<LocationData | null>(initialDraftFilters.origin)
    const [destination, setDestination] = useState<LocationData | null>(initialDraftFilters.destination)
    const [originOptions, setOriginOptions] = useState<LocationData[]>(initialDraftFilters.originOptions ?? [])
    const [destinationOptions, setDestinationOptions] = useState<LocationData[]>(initialDraftFilters.destinationOptions ?? [])
    const [originRadius, setOriginRadius] = useState<number | null>(initialDraftFilters.originRadius)
    const [destinationRadius, setDestinationRadius] = useState<number | null>(initialDraftFilters.destinationRadius)
    const [startDate, setStartDate] = useState<Date | null>(initialDraftFilters.startDate)
    const [endDate, setEndDate] = useState<Date | null>(initialDraftFilters.endDate)
    // Server-side equipment multi-select (box-truck LABELS). Empty = the full class set; a pick narrows the query.
    const [equipmentLabels, setEquipmentLabels] = useState<string[]>(initialDraftFilters.equipmentLabels ?? [])
    // Legacy single-cap drafts stay readable for persisted tabs created before the per-group Weight & size
    // panel. The new per-group values remain source-scoped client refinements so default DB queries do not change.
    const [fullPartial, setFullPartial] = useState<BoxTruckLoadType>(initialDraftFilters.fullPartial ?? 'BOTH')
    const [lengthFt, setLengthFt] = useState<number | null>(initialDraftFilters.lengthFt ?? null)
    const [weightLbs, setWeightLbs] = useState<number | null>(initialDraftFilters.weightLbs ?? null)
    // The request's own freshness bound, chosen in Advanced Filters and sent with the search — see
    // BoxTruckSearchFilters.maxPostedAgeHours.
    const [maxPostedAgeHours, setMaxPostedAgeHours] = useState(() => sanitizeBoxTruckMaxPostedAgeHours(initialDraftFilters.maxPostedAgeHours))
    const [capacityLimits, setCapacityLimits] = useState<BoxTruckCapacityLimits>(() => sanitizeBoxTruckCapacityLimits(initialDraftFilters.capacityLimits))
    const [includeUnknownCapacity, setIncludeUnknownCapacity] = useState(initialDraftFilters.includeUnknownCapacity !== false)
    // Whether the board asks for the live DAT supplement at all. OFF by default, switched from Advanced
    // Filters, and remembered for a day — see useBoxTruckDatEnabled.
    const [datEnabled, setDatEnabled] = useBoxTruckDatEnabled()
    const [beonEnabled, setBeonEnabled] = useBoxTruckBeonEnabled()
    // Whether this project HAS a BeOnCarrier connection, and the modal that makes one. The tri-state it
    // carries is load-bearing in two places: `beonRequestable` must not gate the feed on an answer it does
    // not have yet, and the row keeps its switch until we KNOW there is nothing to switch on.
    const beon = useBoxTruckBeonConnection()
    const [datEquipmentTypes, setDatEquipmentTypes] = useState(() => sanitizeBoxTruckDatSubtypeCodes(initialDraftFilters.datEquipmentTypes))
    // Every PERSISTED board key — each search tab plus the reserved Driver Bids bucket. Shared by the stores
    // that keep one bucket per board, so a closed tab's bucket is dropped from all of them alike.
    //
    // My Biddings is deliberately absent: its client-side filters live for the session only. Driver Bids
    // survives a reload on its own expiry clock (it has no tab to expire with), and giving a second pinned
    // board that treatment means a second stamp — without one, a Min $ set there would persist forever,
    // which is the forgotten-filter case the clock exists to end.
    const validBoardKeys = useMemo(() => [...tabs.map((tab) => tab.id), DRIVER_BIDS_KEY], [tabs])
    // The board's CLIENT-SIDE filters — the refinement values and the Sources-bar chips — restored across a
    // reload. Both are kept per board; see useBoxTruckViewPrefs.
    const { refinementsByTab, setRefinementsByTab, hiddenSourcesByTab, setHiddenSourcesByTab, queryByTab, setQueryByTab } = useBoxTruckViewPrefs({
        validKeys: validBoardKeys,
        driverBidsKey: DRIVER_BIDS_KEY,
        handoffRequested: browserHandoffRequested,
        importedRefinements: importableHandoff?.refinements,
        importedHiddenSources: importableHandoff?.hiddenSources,
        importedQuery: importableHandoff?.query,
        importedKey: importableHandoff ? activeTabId : undefined,
        isolatedWindow,
        persist: handoffReady,
    })
    // Which supplements this board is asking for, from the switches and what each one additionally needs.
    // One place, so the render-time assignment and both toggles cannot disagree about it.
    const requestedSupplements = useCallback(
        (override?: Partial<RequestedSupplements>): RequestedSupplements => ({
            [DAT_SOURCE]: datEnabled,
            [BEON_SOURCE]: beonRequestable(beonEnabled, beon.connectedRef.current),
            ...override,
        }),
        [datEnabled, beonEnabled, beon.connectedRef]
    )
    // What this render's board asks for. One object for the request gate below and the Sources-bar chips, so a
    // feed has a chip exactly while it is being requested.
    const askedSupplements = useMemo<RequestedSupplements>(
        () => ({ [DAT_SOURCE]: datEnabled, [BEON_SOURCE]: beonRequestable(beonEnabled, beon.connected) }),
        [datEnabled, beonEnabled, beon.connected]
    )
    // The request gate is a REF, not state: switching a feed on re-fetches from inside the same handler,
    // before React has re-rendered with the new value. Assigned on every render too, so a change that did
    // not come through a toggle (the status read landing) is picked up.
    excludedSourcesRef.current = excludedSourcesFor(askedSupplements)

    const makeGatedSourceToggle = useGatedSourceToggle({
        excludedSourcesRef,
        forgetGatedSource,
        revealGatedSource,
        dropSnapshot,
        activeTabId,
        getTabs: () => tabsRef.current,
        activeTabSearchedRef,
        setHiddenSourcesByTab,
        unhideSourceOnEveryBoard,
    })

    const toggleDat = useMemo(() => makeGatedSourceToggle(DAT_SOURCE, 'dat_source_toggled', setDatEnabled), [makeGatedSourceToggle, setDatEnabled])
    const onDatEnabledChange = useCallback(
        (enabled: boolean) => toggleDat(enabled, requestedSupplements({ [DAT_SOURCE]: enabled })),
        [toggleDat, requestedSupplements]
    )

    // A board left switched ON asks for the feed while the status read is still in flight, and that is
    // deliberate: withholding a source the operator has on, over an answer we do not have yet, would look
    // exactly like a feed with no loads. What a failure costs is small and bounded — the board treats a
    // supplement's `source_error` as best-effort and keeps rendering Sylectus (`onSourceError`), and the one
    // case said out loud is a REVEAL, i.e. the operator pressing the switch, which is the moment they are
    // actually waiting for an answer.
    const toggleBeon = useMemo(() => makeGatedSourceToggle(BEON_SOURCE, 'beon_source_toggled', setBeonEnabled), [makeGatedSourceToggle, setBeonEnabled])
    /** `justConnected` is for the connect flow: it has made the feed requestable, and saying so here is what
     *  makes the success path independent of whether `markConnected` ran first. */
    const onBeonEnabledChange = useCallback(
        (enabled: boolean, justConnected = false) =>
            // `justConnected || <the stored answer>` IS a connection answer, so this stays one call to the
            // shared predicate rather than a second rule that could disagree with it.
            toggleBeon(enabled, requestedSupplements({ [BEON_SOURCE]: beonRequestable(enabled, justConnected || beon.connectedRef.current) })),
        [toggleBeon, requestedSupplements, beon.connectedRef]
    )
    // Keep secondary board controls out of the initial mobile scan. Desktop ignores this disclosure and
    // always renders the full operator toolbar.
    const [mobileMoreFiltersOpen, setMobileMoreFiltersOpen] = useState(false)
    // "Search Vehicles" drawer (atrek): drivers near a typed address/zip — e.g. where a driver is sitting.
    // Mounted LAZILY on first open (latch): the drawer's hook tree (overlays listeners + state) must not
    // run for operators who never touch the feature; after the first open it stays mounted so the typed
    // origin + results survive close/reopen.
    const [searchVehiclesOpen, setSearchVehiclesOpen] = useState(false)
    const [searchVehiclesMounted, setSearchVehiclesMounted] = useState(false)
    const openSearchVehicles = useCallback(() => {
        setSearchVehiclesMounted(true)
        setSearchVehiclesOpen(true)
    }, [])
    // The picked "Search for driver" drivers PER SEARCH TAB — persisted with the same per-window isolation as
    // the tabs, so a reload restores each tab's driver context without selections bleeding between windows.
    // MULTI: one lane can cover several drivers. The persistence hook scrubs private verification fields.
    const { driversByTab, setDriversByTab, patchDriverLocation } = useBoxTruckDriverSelections({
        validKeys: validBoardKeys,
        activeTabId,
        importedDrivers: importableHandoff?.selectedDrivers,
        handoffRequested: browserHandoffRequested,
        isolatedWindow,
        persist: handoffReady,
    })
    const tabDrivers = driversByTab[activeTabId] ?? EMPTY_DRIVERS
    // Which PINNED board is on screen: Driver Bids (every bid the application's drivers placed) or My
    // Biddings (the loads this dispatcher has emailed a bid about). NEVER the default — null on mount, even
    // with no search; the operator opts in by clicking the pill. One state rather than a flag per board: two
    // booleans can both be true, and every one of the ~50 reads below would have to agree on which wins.
    // Declared here (ahead of the refinements block) so refinements can key off it.
    const [pinnedBoard, setPinnedBoard] = useState<BoxTruckPinnedBoard | null>(null)
    const driverBidsActive = pinnedBoard === 'driver_bids'
    const myBidsActive = pinnedBoard === 'my_bids'
    // "Not a lane search" — true on BOTH pinned boards, which is what every branch below that hides a lane
    // control or a lane-scoped filter is actually asking. Still accurate as a name: both list loads that
    // already carry a bid.
    const bidLoadsOnly = pinnedBoard !== null
    // Driver Bids carries the SAME two filters as a lane tab — picked drivers and equipment — but in its own
    // reserved buckets, so narrowing the bids never rewrites the lane's draft (and vice versa). Both are sent
    // to the server (the tab paginates), not sifted client-side.
    const bidsDrivers = driversByTab[DRIVER_BIDS_KEY] ?? EMPTY_DRIVERS
    const [bidsEquipmentLabels, setBidsEquipmentLabels] = useState<string[]>(EMPTY_EQUIPMENT)
    const bidsDriverIds = useMemo(() => bidsDrivers.map((d) => d.userId), [bidsDrivers])
    // Client-side refinements (load-hub Row-2: trip min/max miles, min rate, min RPM, min weight, age) —
    // applied instantly over the loaded board; a set bound excludes loads missing that field. Kept PER TAB
    // (keyed by tab id, in useBoxTruckViewPrefs above) so each searched lane has its own min/max miles /
    // rate / weight — switching tabs no longer carries one tab's refinements onto another. A search tab with
    // no entry yet defaults to one hour; a pinned board stays unfiltered until the operator narrows it.
    // A pinned board has no tab id of its own (`activeTabId` still points at the last-focused SEARCH tab while
    // it shows), so each gets a RESERVED bucket — otherwise its refinements bind to that lane tab and bleed
    // both ways (a lane's Min $ silently filters the bids board; clearing it there wipes the lane's own
    // refinements), and the two pinned boards would filter each other.
    const refinementKey = boardStateKey(pinnedBoard, activeTabId)
    const defaultRefinements = defaultRefinementsForBoard()
    const refinements = refinementsByTab[refinementKey] ?? defaultRefinements
    // The find term, PER BOARD like the refinements above: text typed on one lane no longer follows the
    // operator to another. Restored as a CLIENT-SIDE row filter only — exactly what an untouched find box
    // already does before Search is pressed — and never fed into the mount search, so a restored Numeo ID
    // cannot turn a lane board into a one-row server lookup.
    const query = queryByTab[refinementKey] ?? ''
    const setQuery = useCallback((next: string) => setQueryByTab((prev) => ({ ...prev, [refinementKey]: next })), [refinementKey, setQueryByTab])
    // Sources bar: a pure client-side hide over the rows already fetched. Every chip behaves the same way,
    // DAT included — what the search ASKS for is decided by the DAT switch, not here, so there is exactly one
    // control per question and a chip can never leave the operator with a feed they cannot get back.
    // PER BOARD, like the refinements and the find term: the chips were built before the board had tabs and
    // stayed global by omission, which meant hiding a feed on one lane silently hid it on every other one.
    // Keyed off refinementKey rather than activeTabId so all three client filters agree on which board they
    // describe — browserTabWorkspace exports the chips unconditionally, so a split key hands a detached tab
    // the lane's chips beside a pinned board's refinements, a pairing no board ever displayed.
    const hiddenSources = hiddenSourcesByTab[refinementKey] ?? EMPTY_HIDDEN
    const onToggleSource = useCallback(
        (key: string) => {
            setHiddenSourcesByTab((prev) => {
                const nextHidden = new Set(prev[refinementKey] ?? EMPTY_HIDDEN)
                // `delete` reports whether the chip WAS off, i.e. whether this click is switching it back on.
                if (!nextHidden.delete(key)) nextHidden.add(key)
                return { ...prev, [refinementKey]: nextHidden }
            })
            trackBoxTruckUiInteraction('source_toggled', key)
        },
        [refinementKey, setHiddenSourcesByTab]
    )
    const patchRefinements = useCallback(
        (patch: Partial<BoxTruckRefinements>) =>
            setRefinementsByTab((prev) => ({ ...prev, [refinementKey]: { ...(prev[refinementKey] ?? defaultRefinements), ...patch } })),
        [defaultRefinements, refinementKey, setRefinementsByTab]
    )
    const commitFilterPatch = useCallback(
        (patch: Partial<BoxTruckSearchFilters>) => {
            const nextFilters = { ...activeTab.filters, ...patch }
            commitFilters(nextFilters, false)
            persistFilters(nextFilters)
        },
        [activeTab.filters, commitFilters, persistFilters]
    )
    /** Clear only what Advanced Filters owns: refinements, load type and DAT subtype choices. The separate
     *  Weight & size panel owns its own Reset action. */
    const resetAdvancedFilters = useCallback(() => {
        trackBoxTruckUiInteraction('advanced_filters_reset', pinnedBoard ?? 'search')
        setRefinementsByTab((prev) => {
            const current = prev[refinementKey] ?? defaultRefinements
            return { ...prev, [refinementKey]: clearAdvancedRefinements(current) }
        })
        setFullPartial('BOTH')
        setLengthFt(null)
        setWeightLbs(null)
        if (!bidLoadsOnly) {
            // Clearing Advanced Filters restores the unnarrowed selection — every subtype ticked. Emptying it
            // would leave the DAT switch on while the board quietly stopped asking DAT for anything.
            const datEquipmentTypes = defaultBoxTruckDatSubtypeCodes()
            const persistedPatch = {
                datEquipmentTypes,
                fullPartial: 'BOTH' as const,
                lengthFt: null,
                weightLbs: null,
                maxPostedAgeHours: BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS,
            }
            setMaxPostedAgeHours(BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS)
            setDatEquipmentTypes(datEquipmentTypes)
            commitFilterPatch(persistedPatch)
        }
    }, [bidLoadsOnly, commitFilterPatch, defaultRefinements, pinnedBoard, refinementKey, setRefinementsByTab])
    const updateCapacityFilters = useCallback(
        (nextLimits: BoxTruckCapacityLimits, nextIncludeUnknown: boolean) => {
            setCapacityLimits(nextLimits)
            setIncludeUnknownCapacity(nextIncludeUnknown)
            commitFilterPatch({ capacityLimits: nextLimits, includeUnknownCapacity: nextIncludeUnknown })
        },
        [commitFilterPatch]
    )
    const resetSecondaryFilters = useCallback(() => {
        trackBoxTruckUiInteraction('secondary_filters_reset', pinnedBoard ?? 'search')
        setRefinementsByTab((prev) => ({ ...prev, [refinementKey]: defaultRefinements }))
        if (bidLoadsOnly) return

        const nextCapacityLimits = createDefaultBoxTruckCapacityLimits()
        const nextDatEquipmentTypes = defaultBoxTruckDatSubtypeCodes()
        setFullPartial('BOTH')
        setLengthFt(null)
        setWeightLbs(null)
        setCapacityLimits(nextCapacityLimits)
        setIncludeUnknownCapacity(true)
        setDatEquipmentTypes(nextDatEquipmentTypes)
        // Reset the LIVE value too, not only the persisted patch below: the control and the next request both
        // read this state, so patching storage alone left the board searching on the old Max Age behind a panel
        // that had just been told it was cleared.
        setMaxPostedAgeHours(BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS)
        // The DAT switch is left alone on purpose: it is a source, not a filter, and it is remembered for a
        // day — turning it off here took the feed away from an operator who only meant to clear Min/Max mi.

        commitFilterPatch({
            capacityLimits: nextCapacityLimits,
            includeUnknownCapacity: true,
            datEquipmentTypes: nextDatEquipmentTypes,
            fullPartial: 'BOTH' as const,
            lengthFt: null,
            weightLbs: null,
            maxPostedAgeHours: BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS,
        })
    }, [bidLoadsOnly, commitFilterPatch, defaultRefinements, pinnedBoard, refinementKey, setRefinementsByTab])
    // Blocked and favorite brokers come from load-hub's my_brokers system (types IN_BLACKLIST / FAVORITE), matched by
    // MC, email or — for Sylectus, which carries no MC — brokerage name (see buildBrokerListMatcher). The same
    // identity serves "Show Only Preferred" as well as the blacklist.
    const { brokers: myBrokers } = useClassifiedBrokers()
    const { isBlockedBroker, isPreferredBroker } = useMemo(
        () => ({
            isBlockedBroker: buildBrokerListMatcher(myBrokers.filter((b) => b.type === BrokerType.IN_BLACKLIST)),
            isPreferredBroker: buildBrokerListMatcher(myBrokers.filter((b) => b.type === BrokerType.FAVORITE)),
        }),
        [myBrokers]
    )
    // Company filter (load-hub semantics) — shared by this user's search tabs within the active company.
    // Which table columns this operator reads a load by — persisted per user (see useBoxTruckColumns).
    const { visibleColumns, columnOrder, toggleColumn, reorderColumns, resetColumns } = useBoxTruckColumns(user?.id)
    const { preferences: boardPreferences, setPreference: setBoardPreference } = useBoxTruckBoardPreferences(user?.id)
    const [settingsOpen, setSettingsOpen] = useState(false)
    const hiddenColumnCount = BOX_TRUCK_COLUMN_DEFS.filter((c) => !visibleColumns[c.id]).length
    const columnWord = hiddenColumnCount === 1 ? 'column' : 'columns'

    const { companyFilter, companyFilterReady, setCompanyFilter } = useBoxTruckCompanyFilter({
        projectName: application?.projectName,
        userId: user?.id,
    })

    const onPinnedBoardSelect = useCallback((board: BoxTruckPinnedBoard) => {
        setPinnedBoard(board)
        // Two events, not one with a board label: the Driver Bids series predates My Biddings and keeping it
        // under its own name is what lets the new board be measured without reinterpreting the old numbers.
        trackBoxTruckUiInteraction(board === 'my_bids' ? 'my_bids_opened' : 'bid_loads_toggled', 'on')
    }, [])
    // Which load is open, PER BOARD (search tab id, or DRIVER_BIDS_KEY) — same keying as driversByTab /
    // refinementsByTab. Derived, not saved-and-restored on switch: one source of truth means a tab can't
    // come back showing a load it no longer has. Leaving a tab and returning re-opens what was open there.
    const [selectedKeyByBoard, setSelectedKeyByBoard] = useState<Record<string, string>>(() =>
        importableHandoff?.selectedLoadKey ? { [activeTabId]: importableHandoff.selectedLoadKey } : {}
    )
    const selectedKey = selectedKeyByBoard[refinementKey] ?? null
    // Writes always land on the CURRENT board. null removes the entry rather than storing it, so the map
    // holds only boards with something open.
    const setSelectedKey = useCallback(
        (key: string | null) => {
            setSelectedKeyByBoard((prev) => {
                if (key == null) {
                    if (!(refinementKey in prev)) return prev
                    const rest = { ...prev }
                    delete rest[refinementKey]
                    return rest
                }
                if (prev[refinementKey] === key) return prev
                return { ...prev, [refinementKey]: key }
            })
        },
        [refinementKey]
    )

    // Which tab the SEARCH board currently on screen belongs to. A switch renders once with the new tab id
    // while the old tab's rows are still mounted (the swap happens in the effect below), and in that render a
    // load that is perfectly present would look missing. Everything that judges "is my load still there?"
    // waits for this to catch up. A pinned board swaps its rows synchronously when opened, so it never waits.
    const [boardTabId, setBoardTabId] = useState(activeTabId)

    // The tab strip's node, captured via a callback ref because it mounts LATE — the browser-tab handoff gate
    // returns before this page's markup exists — so a plain ref read in an effect would measure null and never
    // retry. Measured further down, once we know whether the drawer is even open.
    const [tabsBarEl, setTabsBarEl] = useState<HTMLDivElement | null>(null)
    // Pickup is a REQUIRED filter (defaults to today) — mirrors Load Hub. A Search with the window cleared
    // flags the field instead of firing an unbounded (and much slower) anywhere-any-date query.
    const [pickupError, setPickupError] = useState(false)

    // One page-view event per usable mount. A handoff waiting to switch companies must not attribute a page
    // view (or any search) to the wrong tenant during the brief pre-switch render.
    useEffect(() => {
        if (handoffReady) trackBoxTruckPageViewed()
    }, [handoffReady])

    // On a TAB SWITCH: reset the draft inputs to the new tab's lane, then show its board — re-fetch if
    // searched, else clear to the "New search" state. Ref-read so the effect fires only on activeTabId change.
    const activeTabRef = useRef(activeTab)
    activeTabRef.current = activeTab
    const tabsRef = useRef(tabs)
    tabsRef.current = tabs
    const boardScrollTopsRef = useRef(new Map<string, number>())
    const readBoardScrollTop = useCallback((key: string) => boardScrollTopsRef.current.get(key) ?? 0, [])
    const writeBoardScrollTop = useCallback((key: string, scrollTop: number) => {
        if (!isPinnedBoardKey(key) && !tabsRef.current.some((tab) => tab.id === key)) return
        boardScrollTopsRef.current.set(key, scrollTop)
    }, [])
    const prevTabIdRef = useRef(activeTabId)
    useEffect(() => {
        const prevId = prevTabIdRef.current
        if (prevId === activeTabId) return
        // Freeze the OUTGOING tab's board so switching back to it is instant — but only if it still exists
        // (a closed tab is dropped from the cache, and re-saving it here would resurrect a dead entry).
        if (tabsRef.current.some((t) => t.id === prevId)) saveSnapshot(prevId)
        prevTabIdRef.current = activeTabId
        const tab = activeTabRef.current
        setOrigin(tab.filters.origin)
        setDestination(tab.filters.destination)
        setOriginOptions(tab.filters.originOptions ?? [])
        setDestinationOptions(tab.filters.destinationOptions ?? [])
        setOriginRadius(tab.filters.originRadius)
        setDestinationRadius(tab.filters.destinationRadius)
        setStartDate(tab.filters.startDate)
        setEndDate(tab.filters.endDate)
        setEquipmentLabels(tab.filters.equipmentLabels ?? [])
        setDatEquipmentTypes(sanitizeBoxTruckDatSubtypeCodes(tab.filters.datEquipmentTypes))
        setCapacityLimits(sanitizeBoxTruckCapacityLimits(tab.filters.capacityLimits))
        setIncludeUnknownCapacity(tab.filters.includeUnknownCapacity !== false)
        setFullPartial(tab.filters.fullPartial ?? 'BOTH')
        setLengthFt(tab.filters.lengthFt ?? null)
        setWeightLbs(tab.filters.weightLbs ?? null)
        setMaxPostedAgeHours(sanitizeBoxTruckMaxPostedAgeHours(tab.filters.maxPostedAgeHours))
        setPickupError(false)
        // Searched tab: thaw its cached board instantly; only re-stream if this tab has no snapshot yet.
        if (tab.searched) {
            if (!restoreSnapshot(activeTabId)) search(tab.filters)
        } else {
            clear()
        }
        setBoardTabId(activeTabId)
    }, [activeTabId, search, clear, saveSnapshot, restoreSnapshot])

    // On mount, re-run the RESTORED active tab's bounded lane search if it was searched (localStorage) — an
    // unsearched tab stays on the "New search" state, so a fresh visit still fires no request.
    const didMountRef = useRef(false)
    useEffect(() => {
        if (didMountRef.current || !handoffReady) return
        didMountRef.current = true
        if (activeTabRef.current.searched) {
            const numeoId = importableHandoff ? normalizeNumeoIdQuery(importableHandoff.query) : activeTabRef.current.filters.numeoId
            search({ ...activeTabRef.current.filters, numeoId })
        }
    }, [handoffReady, importableHandoff, search])

    // The two pinned boards, each idle unless its pill is the active board. One hook, two sources: what
    // differs between them is the question (which server list, and which live events add rows to it), not
    // the mechanics of paging and merging it — see useBoxTruckBidFeed. The source objects are rebuilt per
    // render by design; the hook keys its fetching on `source.key`, never on the object's identity.
    const driverBids = useBoxTruckBidFeed(driverBidsActive, driverBidFeedSource(bidsDriverIds, bidsEquipmentLabels))
    const myBids = useBoxTruckBidFeed(myBidsActive, myBidFeedSource(user?.id))
    // The pinned feed on screen, or null while a search tab is. Both share one shape (BoxTruckBidFeed) so
    // this is picked ONCE: a per-value ternary per board is where the board that is not on screen gets read
    // by mistake.
    const activeFeed = driverBidsActive ? driverBids : myBidsActive ? myBids : null
    const boardLoads = activeFeed ? activeFeed.loads : loads
    const boardBids = activeFeed ? activeFeed.bids : bids
    const boardOperatorBids = activeFeed ? activeFeed.operatorBids : operatorBids
    // A pinned board is its own feed, so its Contact-column thread state must come from ITS loads — the
    // search tabs' overlay map only covers whatever was last searched.
    const boardEmailThreads = activeFeed ? activeFeed.emailThreads : emailThreads
    /**
     * Loads with a driver bid nobody on this desk has emailed a broker about (ADR-057 §13).
     *
     * The Driver Bids board gets this from the server, which is the only place that can answer it for loads
     * the search is not showing. On a SEARCH tab it is read off the overlays the board already has: a load
     * with driver bids and no thread of this desk's own. That is the same question, asked of the rows in
     * front of the operator — and it is what pins them to the top, because a bid at the bottom of a list
     * sorted by arrival is a driver waiting on nobody.
     */
    const searchAwaitingDesk = useMemo(() => {
        const waiting = new Set<string>()
        // BOTH overlays or neither, PER LOAD. They are separate requests, and one can answer for some rows
        // and not others — a failed slice, an appended page. With the bids back and the threads not, a bid
        // load answered "nobody has emailed about this" and jumped to the top, then jumped back down when
        // the threads landed, on the one list the operator is reading. A load we have not heard about yet is
        // not pinned and not un-pinned; it waits.
        for (const [key, placed] of Object.entries(bids)) {
            if (placed.length && threadsKnownFor.has(key) && !emailThreads[key]) waiting.add(key)
        }
        return waiting
    }, [bids, emailThreads, threadsKnownFor])
    const boardAwaitingDesk = activeFeed ? activeFeed.awaitingDesk : searchAwaitingDesk
    const boardLoading = activeFeed ? activeFeed.loading : loading
    // False for the single render between "active tab changed" and "that tab's rows are mounted" — and, on a
    // pinned board, until that feed has actually answered once. Opening one CLEARS its loads synchronously but
    // only starts the fetch from an effect, so the render in between shows an empty board that has proven
    // nothing; assuming it settled there would flash the re-verify banner at a live bid load.
    const boardSettled = activeFeed ? activeFeed.answered : boardTabId === activeTabId

    // A Numeo-ID find term goes to the server (exact, cross-page); other text is a client filter (numeoId null).
    // Persist + track + commit + fire a search for the fully-built draft when the operator presses Search.
    const submitSearch = useCallback(
        (filters: BoxTruckSearchFilters) => {
            // Persist the searched lane/radius/date/equipment set so a page reload restores it.
            persistFilters(filters)
            trackBoxTruckSearchSubmitted({
                origin: filters.origin,
                destination: filters.destination,
                originRadius: filters.originRadius,
                destinationRadius: filters.destinationRadius,
                startDate: filters.startDate,
                endDate: filters.endDate,
                equipmentLabels: filters.equipmentLabels ?? [],
                bidLoadsOnly,
                datEnabled,
                datEquipmentTypes: filters.datEquipmentTypes ?? [],
                capacityLimits: filters.capacityLimits ?? createDefaultBoxTruckCapacityLimits(),
                maxPostedAgeHours: sanitizeBoxTruckMaxPostedAgeHours(filters.maxPostedAgeHours),
                // Asked of the builder itself. Counting chips here looked equivalent and was not — see
                // `countOriginLanes`.
                originPoints: countOriginLanes(filters),
                viaSearchStream: viaSearchStreamRef.current,
            })
            // Commit the lane onto the active tab (flags it searched → swaps the "New search" state for the board).
            commitFilters(filters, true)
            search({ ...filters, numeoId: normalizeNumeoIdQuery(query) })
        },
        [persistFilters, commitFilters, search, query, bidLoadsOnly, datEnabled, viaSearchStreamRef]
    )

    const runSearch = useCallback(() => {
        // Pickup is required — block the search and flag the field if the operator cleared the window.
        if (!startDate || !endDate) {
            setPickupError(true)
            return
        }
        setPinnedBoard(null)
        // The tab KEEPS its picked driver across a plain search: refining the lane (radius, dates, equipment,
        // a second origin) is how an operator hunts loads FOR that driver, so dropping the pick here would
        // unpin them from Nearby Drivers and empty the Copy Load {{driver_*}} variables mid-hunt. The pick is
        // cleared explicitly (the picker's clear button) or by leaving the tab.
        submitSearch({
            origin,
            destination,
            originOptions,
            destinationOptions,
            originRadius,
            destinationRadius,
            startDate,
            endDate,
            equipmentLabels,
            datEquipmentTypes,
            capacityLimits,
            includeUnknownCapacity,
            fullPartial,
            lengthFt,
            weightLbs,
            maxPostedAgeHours,
        })
    }, [
        submitSearch,
        origin,
        destination,
        originOptions,
        destinationOptions,
        originRadius,
        destinationRadius,
        startDate,
        endDate,
        equipmentLabels,
        datEquipmentTypes,
        capacityLimits,
        includeUnknownCapacity,
        fullPartial,
        lengthFt,
        weightLbs,
        maxPostedAgeHours,
    ])

    // "Search for driver": prefill origin = the picked drivers' last GPS fixes and equipment = their
    // equipment (or "All") and reflect it in the visible draft. Guarded to locatable
    // drivers (the picker disables no-GPS rows). A pick rewrites ONLY origin + equipment: the operator's
    // radius and pickup window are carried over untouched, so picking a driver can't silently widen a 100 mi
    // lane back to 150 or snap a week-out window back to today. Only an UNSET radius/window falls back — to
    // the same defaults a fresh single-city pick uses — because a driver's GPS point with no radius searches
    // nothing.
    const handleDriverToggle = useCallback(
        (driver: BoxTruckVerification) => {
            if (!driver.lastLocation) return
            const current = driversByTab[activeTabId] ?? EMPTY_DRIVERS
            const next = current.some((d) => d.userId === driver.userId) ? current.filter((d) => d.userId !== driver.userId) : [...current, driver]
            setDriversByTab((prev) => ({ ...prev, [activeTabId]: next }))
            // Un-picking the LAST driver leaves the lane exactly as it is (same as the picker's clear ✕) —
            // the operator keeps the board they were reading instead of it snapping back to anywhere.
            if (next.length === 0) return
            setPinnedBoard(null)
            // ONE SEARCH PER DRIVER. Picking three trucks asks what those three can take, so the lane becomes
            // each driver's own fix and the board runs a radius search around each (buildRequests fans them
            // out). It used to collapse to the STATES those fixes sat in and drop the radius, which answered a
            // different question: a 300-mile-wide state around a truck that can realistically serve 150.
            //
            // A fix that never reverse-geocoded has no coordinates to search around. Those drivers fall back
            // to their state so they still contribute; a pick with no usable location at all leaves the lane
            // alone rather than searching everywhere.
            const fixes = next.map((d) => d.lastLocation).filter((loc): loc is NonNullable<typeof loc> => !!loc)
            const withCoords = fixes.filter((loc) => loc.lat != null && loc.lng != null)
            const statesWithoutCoords = [
                ...new Set(
                    fixes
                        .filter((loc) => loc.lat == null || loc.lng == null)
                        .map((loc) => loc.state?.trim().toUpperCase())
                        .filter((st): st is string => !!st)
                ),
            ]
            const options: LocationData[] = [
                ...withCoords.map((loc) => ({ city: loc.city || '', stateProv: loc.state || '', postalCode: '', latitude: loc.lat, longitude: loc.lng })),
                ...statesWithoutCoords.map((stateProv) => ({ stateProv })),
            ]
            if (options.length === 0) return
            // The radius applies as long as at least one pick is a real point; a state-only fallback ignores it.
            const radius = withCoords.length > 0 ? (originRadius ?? DEFAULT_CITY_RADIUS_MI) : null

            const pickupStart = startDate ?? getToday()
            // An unset end falls back to the SAME week-long default a fresh board opens on, not to the start
            // day — otherwise picking a driver on a board whose window was cleared would search a single day.
            const pickupEnd = endDate ?? getDefaultPickupEnd()
            // ADDS the drivers' classes, never replaces what the operator ticked. This used to overwrite the
            // selection, so ticking Cargo Van + Sprinter and then picking cargo-van drivers switched Sprinter
            // off — half the requested search dropped, with nothing on screen saying why.
            const nextEquipmentLabels = withDriverEquipment(equipmentLabels, next)
            setOrigin(options[0])
            setOriginOptions(options)
            setOriginRadius(radius)
            setEquipmentLabels(nextEquipmentLabels)
            setStartDate(pickupStart)
            setEndDate(pickupEnd)
            setPickupError(false)
            commitFilterPatch({
                origin: options[0],
                originOptions: options,
                originRadius: radius,
                equipmentLabels: nextEquipmentLabels,
                startDate: pickupStart,
                endDate: pickupEnd,
            })
            // Name the tab after the pick (same display-name fallback as the picker rows) — one driver by
            // name, several as "first +N". The operator can rename it any time (double-click the tab).
            const firstName = next[0].boxTruckInfo.info?.name || next[0].name || next[0].email
            if (firstName) renameTab(activeTabId, next.length > 1 ? `${firstName} +${next.length - 1}` : firstName)
            // Saved, but not searched: an operator may toggle several drivers while the picker stays open, and
            // each intermediate selection must not replace the board. Search applies the completed
            // origin/equipment selection once.
        },
        [driversByTab, originRadius, startDate, endDate, equipmentLabels, renameTab, activeTabId, setDriversByTab, commitFilterPatch]
    )
    // Driver Bids' own driver toggle: it only NARROWS the bids list (server-side) — no origin/equipment
    // prefill, no lane search. The list refetches off the selection via useBoxTruckBidOnlyLoads.
    const handleBidsDriverToggle = useCallback(
        (driver: BoxTruckVerification) => {
            setDriversByTab((prev) => {
                const current = prev[DRIVER_BIDS_KEY] ?? EMPTY_DRIVERS
                const next = current.some((d) => d.userId === driver.userId) ? current.filter((d) => d.userId !== driver.userId) : [...current, driver]
                return { ...prev, [DRIVER_BIDS_KEY]: next }
            })
        },
        [setDriversByTab]
    )

    // Refresh reloads whichever source is active: the bid-loads list when "Bid loads" is on, else a QUIET
    // re-fetch of the active search (no board blanking). Callers: Auto Refresh, and the manual button that
    // now only the Driver Bids tab renders — a search tab reloads through Search itself.
    const refresh = activeFeed ? activeFeed.reload : reloadFeed
    const onManualRefresh = useCallback(() => {
        trackBoxTruckUiInteraction('refresh_clicked')
        refresh()
    }, [refresh])

    // A pinned board has its own list to catch up, and it is gated on the same condition as the Refresh
    // button rather than on the lane tab behind it — otherwise reconnecting while My Biddings is open only
    // refetched if some lane tab happened to have searched.
    useReconnectReconcile(refresh, bidLoadsOnly || !!activeTab.searched)

    // Keep the ACTIVE tab's live subscriptions alive, and revive them when switching back to a tab whose
    // subscriptions lapsed off screen. Gated on a searched tab that is actually showing a search: the Bid
    // loads view has no live search to renew, and its `refresh` is a different fetch entirely.
    useLiveSearchRenewal(activeTabId ? liveSearchLifetimes[boardSearchInstanceId(activeTabId)] : undefined, refresh, !!activeTab.searched && !bidLoadsOnly)

    // Every lane filter below is saved onto the tab the moment it changes, like Weight & size and the DAT
    // types: the tab is what a reload or a tab switch restores from, so a value left in page state until
    // Search was lost by both. Saving does not search — the board keeps its rows until Search is pressed.
    const handlePickupChange = useCallback(
        (start: Date | null, end: Date | null) => {
            setStartDate(start)
            setEndDate(end)
            if (start && end) setPickupError(false)
            commitFilterPatch({ startDate: start, endDate: end })
        },
        [commitFilterPatch]
    )

    // A city pick (coords) defaults to a radius so the leg is an actual geo search, not a silent whole-state
    // one — matching the mobile board (DEFAULT_CITY_DEADHEAD). A kept radius wins; a state-only pick or a
    // cleared field has no point, so drop any stale radius.
    const handleOriginChange = useCallback(
        (options: LocationData[]) => {
            // Mirror the first city (else first chip) for the radius + legacy readers. A radius applies to any
            // pick that carries coordinates — one city, or one per picked driver; a state-only selection has no
            // point to measure from, so it drops a stale radius.
            const firstCity = options.find((o) => o.city && o.latitude != null) ?? options[0] ?? null
            const hasPoint = options.some((o) => o.latitude != null && o.longitude != null)
            const radius = hasPoint ? (originRadius ?? DEFAULT_CITY_RADIUS_MI) : null
            setOriginOptions(options)
            setOrigin(firstCity)
            setOriginRadius(radius)
            commitFilterPatch({ originOptions: options, origin: firstCity, originRadius: radius })
        },
        [commitFilterPatch, originRadius]
    )
    const handleDestinationChange = useCallback(
        (options: LocationData[]) => {
            const firstCity = options.find((o) => o.city && o.latitude != null) ?? options[0] ?? null
            const hasPoint = options.some((o) => o.latitude != null && o.longitude != null)
            const radius = hasPoint ? (destinationRadius ?? DEFAULT_CITY_RADIUS_MI) : null
            setDestinationOptions(options)
            setDestination(firstCity)
            setDestinationRadius(radius)
            commitFilterPatch({ destinationOptions: options, destination: firstCity, destinationRadius: radius })
        },
        [commitFilterPatch, destinationRadius]
    )
    const handleOriginRadiusChange = useCallback(
        (radius: number | null) => {
            setOriginRadius(radius)
            commitFilterPatch({ originRadius: radius })
        },
        [commitFilterPatch]
    )
    const handleDestinationRadiusChange = useCallback(
        (radius: number | null) => {
            setDestinationRadius(radius)
            commitFilterPatch({ destinationRadius: radius })
        },
        [commitFilterPatch]
    )
    const handleEquipmentChange = useCallback(
        (labels: string[]) => {
            setEquipmentLabels(labels)
            commitFilterPatch({ equipmentLabels: labels })
        },
        [commitFilterPatch]
    )

    // The driver and equipment filters the ACTIVE board actually supports, resolved in ONE place so the
    // filter bar and the drawer's pinned rows cannot disagree about whose selection is showing.
    //
    // A lane tab owns its own pair. Driver Bids has its own pair in reserved buckets, both sent to the
    // server. My Biddings has NEITHER — its endpoint answers "loads I emailed" and takes no narrowing — so
    // it passes no handler, which is how BoxTruckFilters decides not to render a control at all. Falling
    // through to the lane tab's values instead would put a live control on screen that edits the board the
    // operator is not looking at.
    const boardFilterControls = myBidsActive
        ? { equipmentLabels: EMPTY_EQUIPMENT, onEquipmentChange: undefined, selectedDrivers: EMPTY_DRIVERS, onDriverToggle: undefined }
        : driverBidsActive
          ? {
                equipmentLabels: bidsEquipmentLabels,
                onEquipmentChange: setBidsEquipmentLabels,
                selectedDrivers: bidsDrivers,
                onDriverToggle: handleBidsDriverToggle,
            }
          : { equipmentLabels, onEquipmentChange: handleEquipmentChange, selectedDrivers: tabDrivers, onDriverToggle: handleDriverToggle }

    // Derived board view: client equipment refinement, the Sources-bar groups, and the visible
    // (text/hidden-source filtered) rows.
    // A pinned board is source-agnostic: the lane feed's per-source totals (and any hidden-source toggles)
    // belong to the search board — without this, an empty bids list still grew a stale "Sylectus (9)" chip
    // from the last lane search.
    // Suggestion source for the company search — the unique names actually on the loaded board.
    const boardCompanies = useMemo(() => [...new Set(boardLoads.map((l) => l.brokerCompanyName).filter((n): n is string => !!n))].sort(), [boardLoads])

    const { sourceGroups, visibleLoads } = useBoxTruckBoardView({
        boardLoads,
        equipmentLabels: bidLoadsOnly ? EMPTY_EQUIPMENT : equipmentLabels,
        // Per-group capacity filters belong only to the Box Truck Sylectus and DAT feeds. A pinned board has
        // no lane filters at all, so it passes none.
        fullPartial: bidLoadsOnly ? undefined : fullPartial,
        lengthFt: bidLoadsOnly ? null : lengthFt,
        weightLbs: bidLoadsOnly ? null : weightLbs,
        capacityLimits: bidLoadsOnly ? undefined : capacityLimits,
        includeUnknownCapacity: bidLoadsOnly ? undefined : includeUnknownCapacity,
        hiddenSources: bidLoadsOnly ? EMPTY_HIDDEN : hiddenSources,
        query,
        refinements,
        // The broker block/prefer filters belong to the SEARCH board only. A pinned board lists bids already
        // placed — by a driver, or by this dispatcher — and hiding one because its broker later got
        // blacklisted would make a placed bid silently vanish from the view meant to track it.
        isBlockedBroker: bidLoadsOnly ? undefined : isBlockedBroker,
        isPreferredBroker: bidLoadsOnly ? undefined : isPreferredBroker,
        companyFilter: bidLoadsOnly ? undefined : companyFilter,
        requestedSupplements: bidLoadsOnly ? undefined : askedSupplements,
    })

    const hasAdditionalFilters = hasActiveBoxTruckSecondaryFilters({
        query,
        refinements,
        // The ACTIVE board's own pick. Asking `bidLoadsOnly` for it would count Driver Bids' drivers while My
        // Biddings is showing — a "More filters" badge pointing at a selection made on another board, with no
        // control on screen to clear it.
        selectedDriverCount: boardFilterControls.selectedDrivers.length,
        lane: bidLoadsOnly
            ? undefined
            : {
                  companyFilter,
                  defaultCityRadiusMi: DEFAULT_CITY_RADIUS_MI,
                  destinationRadius,
                  fullPartial,
                  hiddenSourceCount: hiddenSources.size,
                  maxPostedAgeHours,
                  capacityLimits,
                  datEnabled,
                  datEquipmentTypes,
                  equipmentLabels,
                  lengthFt,
                  originRadius,
                  weightLbs,
              },
    })
    const hasResettableSecondaryFilters = hasResettableBoxTruckSecondaryFilters({
        refinements,
        defaultRefinements,
        pinnedBoardActive: bidLoadsOnly,
        lane: { fullPartial, lengthFt, weightLbs, maxPostedAgeHours, capacityLimits, includeUnknownCapacity, datEquipmentTypes, equipmentLabels },
    })

    // What the drawer shows for the ACTIVE board, and whether to warn that the load may be dead. The whole
    // decision table (auto-refresh churn vs the operator's own filters vs a mid-switch board) lives in
    // resolveSelectedLoad so it can be tested without mounting this page — see utils/selectedLoad.ts.
    // The cache is committed in an effect rather than during render: a discarded render then cannot leave a
    // snapshot behind that the UI never actually showed.
    const selectedLoadCacheRef = useRef<SelectedLoadCache>({})
    const selection = useMemo(
        () =>
            resolveSelectedLoad({
                selectedKey,
                boardKey: refinementKey,
                visibleLoads,
                boardLoads,
                boardSettled,
                boardLoading,
                previousCache: selectedLoadCacheRef.current,
            }),
        [selectedKey, refinementKey, visibleLoads, boardLoads, boardSettled, boardLoading]
    )
    useEffect(() => {
        selectedLoadCacheRef.current = selection.cache
    }, [selection])
    const selectedLoad = selection.load
    const selectedLoadStale = selection.stale

    // The drawer is viewport-fixed, so it cannot measure the tab strip above it — this page hands it the strip's
    // BOTTOM EDGE in viewport coordinates. Not its height: the two are only equal when the strip sits flush at
    // y=0, which it does not below 900px (MasterLayout pads MainContent down by the fixed <Header>) nor at any
    // breakpoint while a <Warnings> alert is up. Clamped at 0 so a strip scrolled off the top gives a full-height
    // drawer rather than a negative offset.
    // Re-measured on scroll and resize as well as via ResizeObserver, because the edge MOVES without the strip
    // ever changing size: page scroll below 900px, a warning appearing mid-session, warning text re-wrapping.
    // Only tracked while a load is open — nothing reads the offset otherwise, and this keeps the listeners off
    // the board during normal scanning.
    const drawerOpen = selectedLoad != null
    const [drawerTopOffset, setDrawerTopOffset] = useState(0)
    useLayoutEffect(() => {
        if (!tabsBarEl || !drawerOpen) return
        let frame = 0
        const measure = () => {
            frame = 0
            const bottom = Math.max(0, Math.round(tabsBarEl.getBoundingClientRect().bottom))
            setDrawerTopOffset((prev) => (prev === bottom ? prev : bottom))
        }
        const schedule = () => {
            if (frame) return
            frame = requestAnimationFrame(measure)
        }
        measure()
        const observer = new ResizeObserver(schedule)
        observer.observe(tabsBarEl)
        // `scroll` does not bubble, so capture it — the page scrolls inside MasterLayout's MainContent, not window.
        window.addEventListener('scroll', schedule, { capture: true, passive: true })
        window.addEventListener('resize', schedule)
        return () => {
            if (frame) cancelAnimationFrame(frame)
            observer.disconnect()
            window.removeEventListener('scroll', schedule, { capture: true })
            window.removeEventListener('resize', schedule)
        }
    }, [tabsBarEl, drawerOpen])

    const handleRowClick = useCallback(
        (load: CanonicalLoad) => {
            const key = loadKey(load)
            trackBoxTruckLoadOpened(load, { hasBids: (boardBids[key]?.length ?? 0) > 0, hasRating: !!annotations[key]?.rating })
            setSelectedKey(key)
        },
        [boardBids, annotations, setSelectedKey]
    )

    // Copy the ACTIVE search workspace into an isolated top-level browser tab. The storage write and
    // window.open both happen synchronously inside the click gesture so mobile in-app browsers (Telegram
    // included) may open their native tab UI instead of treating it as an unsolicited popup.
    const browserTabWorkspace = useMemo(
        () => ({
            tab: {
                filters: activeTab.filters,
                searched: activeTab.searched,
                label: activeTab.label,
            },
            // Preserve both halves of the current UI: the applied filters behind the result set above, and
            // any newer edits still visible in the controls but not submitted yet.
            draftFilters: {
                origin,
                destination,
                originOptions,
                destinationOptions,
                originRadius,
                destinationRadius,
                startDate,
                endDate,
                equipmentLabels,
                datEquipmentTypes,
                capacityLimits,
                includeUnknownCapacity,
                fullPartial,
                lengthFt,
                weightLbs,
                maxPostedAgeHours,
                numeoId: null,
            },
            query,
            refinements,
            hiddenSources: [...hiddenSources],
            selectedDrivers: tabDrivers,
            selectedLoadKey: selectedKey,
        }),
        [
            activeTab.filters,
            activeTab.label,
            activeTab.searched,
            destination,
            destinationOptions,
            destinationRadius,
            endDate,
            equipmentLabels,
            datEquipmentTypes,
            capacityLimits,
            includeUnknownCapacity,
            fullPartial,
            hiddenSources,
            lengthFt,
            maxPostedAgeHours,
            origin,
            originOptions,
            originRadius,
            query,
            refinements,
            selectedKey,
            startDate,
            tabDrivers,
            weightLbs,
        ]
    )

    const handleOpenSearchInBrowserTab = useCallback(() => {
        openBrowserTab(browserTabWorkspace)
    }, [browserTabWorkspace, openBrowserTab])

    // Middle-click uses the same isolated handoff as the tab-strip action. This preserves the exact detached
    // workspace even when the source is itself isolated, then opens the requested load's drawer in the target.
    const handleOpenInNewTab = useCallback(
        (load: CanonicalLoad) => openBrowserTab({ ...browserTabWorkspace, selectedLoadKey: loadKey(load) }),
        [browserTabWorkspace, openBrowserTab]
    )

    // Consume a `?selectLoad=<loadKey>` deep-link (from a middle-click open-in-new-tab): once the restored board
    // contains that load, open its detail drawer — once. A load that dropped/expired before the board rebuilt
    // just leaves the board open with no drawer.
    const selectLoadKey = searchParams.get('selectLoad')
    const selectLoadConsumedRef = useRef(false)
    useEffect(() => {
        if (!selectLoadKey || selectLoadConsumedRef.current) return
        if (boardLoads.some((l) => loadKey(l) === selectLoadKey)) {
            selectLoadConsumedRef.current = true
            setSelectedKey(selectLoadKey)
        }
    }, [selectLoadKey, boardLoads, setSelectedKey])

    // Infinite scroll: the virtualized table fires endReached as the operator nears the bottom — the live feed
    // (loadMore) or the active pinned board's paginated list (activeFeed.loadMore) depending on mode. All are
    // guarded, so firing on every near-bottom event is safe (they no-op while a fetch is in flight, at the
    // end of the list, or fully loaded).
    const boardLoadMore = activeFeed ? activeFeed.loadMore : loadMore
    // Reads the current note through a ref rather than depending on `annotations`, which gets a new identity
    // on every overlay fetch — so the inline star click does not re-create itself as the board refreshes.
    const annotationsRef = useLatestRef(annotations)
    const handleRate = useCallback(
        (load: CanonicalLoad, rating: number) => void setAnnotation(load, rating, annotationsRef.current[loadKey(load)]?.comment ?? null, 'rating'),
        [annotationsRef, setAnnotation]
    )
    const boardLoadingMore = activeFeed ? activeFeed.loadingMore : loadingMore

    // Closing a tab forgets its cached board, its picked driver, and its refinements. The personal broker
    // filter deliberately survives because every search tab shares it.
    const onRemoveTab = useCallback(
        (id: string) => {
            dropSnapshot(id)
            // Closed, not merely uncached: its live subscriptions go too, in one request.
            releaseBoard(id)
            boardScrollTopsRef.current.delete(id)
            // Every per-board record forgets this tab through the SAME helper, so none of them can be
            // missed when a new one is added. Leaving an entry behind would keep a closed tab's state
            // (and, for the last one, its cached load snapshot below) alive for the lifetime of the page.
            setDriversByTab((prev) => omitBoard(prev, id))
            setRefinementsByTab((prev) => omitBoard(prev, id))
            setHiddenSourcesByTab((prev) => omitBoard(prev, id))
            setQueryByTab((prev) => omitBoard(prev, id))
            setSelectedKeyByBoard((prev) => omitBoard(prev, id))
            selectedLoadCacheRef.current = omitBoard(selectedLoadCacheRef.current, id)
            removeTab(id)
        },
        [dropSnapshot, releaseBoard, removeTab, setDriversByTab, setRefinementsByTab, setHiddenSourcesByTab, setQueryByTab]
    )
    const onCloseAllTabs = useCallback(() => {
        const tabIds = tabsRef.current.map((tab) => tab.id)
        tabIds.forEach(dropSnapshot)
        tabIds.forEach(releaseBoard)
        tabIds.forEach((tabId) => boardScrollTopsRef.current.delete(tabId))
        // Forget the boards being CLOSED, not every board there is. A pinned board is reserved rather than a
        // tab, so it is never one of them and keeps what the operator set on it — which is what
        // setSelectedKeyByBoard already did, and what closing a single tab has always done. Sweeping these
        // records whole made the five disagree: close all tabs with a Min $ on Driver Bids and the Min $
        // vanished while the load it had open stayed. Session-only that was invisible, since everything
        // reset on the next reload; persisted, it comes back as a board half-cleared for no stated reason.
        setDriversByTab((prev) => omitBoards(prev, tabIds))
        setRefinementsByTab((prev) => omitBoards(prev, tabIds))
        setHiddenSourcesByTab((prev) => omitBoards(prev, tabIds))
        setQueryByTab((prev) => omitBoards(prev, tabIds))
        setSelectedKeyByBoard((prev) => omitBoards(prev, tabIds))
        selectedLoadCacheRef.current = omitBoards(selectedLoadCacheRef.current, tabIds)
        closeAll()
    }, [dropSnapshot, releaseBoard, closeAll, setDriversByTab, setRefinementsByTab, setHiddenSourcesByTab, setQueryByTab])

    // Selecting (or adding) a search tab leaves whichever pinned board is showing. Re-clicking the
    // already-active search tab is a no-op for the tabs hook but still exits the pinned board back to that
    // tab's own board.
    const onSelectSearchTab = useCallback(
        (id: string) => {
            setPinnedBoard(null)
            switchTab(id)
        },
        [switchTab]
    )
    const onAddSearchTab = useCallback(() => {
        setPinnedBoard(null)
        addTab()
    }, [addTab])

    if (handoffWaitingForAuth || handoffNeedsCompanySwitch) {
        return (
            <BoxTruckBrowserTabGate
                error={handoffSwitchError}
                switching={handoffSwitching}
                onRetry={retryHandoffSwitch}
                onOpenFresh={openFreshIsolatedSearch}
            />
        )
    }

    return (
        <div className="flex min-h-full min-w-0 flex-col bg-background text-foreground min-[900px]:h-full">
            <div ref={setTabsBarEl} className="border-b border-border px-0 pt-2 min-[900px]:px-4">
                <BoxTruckTabsBar
                    tabs={tabs}
                    activeTabId={activeTabId}
                    activeCount={visibleLoads.length}
                    onSelect={onSelectSearchTab}
                    onRemove={onRemoveTab}
                    onAdd={onAddSearchTab}
                    onCloseAll={onCloseAllTabs}
                    pinnedBoard={pinnedBoard}
                    onPinnedSelect={onPinnedBoardSelect}
                    onReorder={reorderTabs}
                    onRename={renameTab}
                    onOpenActiveTab={handleOpenSearchInBrowserTab}
                />
            </div>

            {(openBrowserTabError || handoffNotice) && (
                <div role="alert" className="flex items-center gap-3 border-b border-warning/30 bg-warning-soft px-4 py-2 text-sm text-foreground">
                    <span className="min-w-0 flex-1">{openBrowserTabError || handoffNotice}</span>
                    <button
                        type="button"
                        aria-label="Dismiss browser tab message"
                        onClick={() => {
                            dismissOpenBrowserTabError()
                            dismissHandoffNotice()
                        }}
                        className="flex size-7 shrink-0 items-center justify-center rounded-md text-foreground-muted hover:bg-default hover:text-foreground"
                    >
                        <X className="size-4" aria-hidden="true" />
                    </button>
                </div>
            )}

            <div className="border-b border-border bg-surface min-[900px]:bg-transparent">
                <BoxTruckFilters
                    originOptions={originOptions}
                    destinationOptions={destinationOptions}
                    originRadius={originRadius}
                    destinationRadius={destinationRadius}
                    startDate={startDate}
                    endDate={endDate}
                    onOriginChange={handleOriginChange}
                    onDestinationChange={handleDestinationChange}
                    onOriginRadiusChange={handleOriginRadiusChange}
                    onDestinationRadiusChange={handleDestinationRadiusChange}
                    onPickupChange={handlePickupChange}
                    pickupError={pickupError}
                    onSearch={runSearch}
                    loading={boardLoading}
                    query={query}
                    onQueryChange={setQuery}
                    hasAdditionalFilters={hasAdditionalFilters}
                    isolatedWindow={isolatedWindow}
                    equipmentLabels={boardFilterControls.equipmentLabels}
                    onEquipmentChange={boardFilterControls.onEquipmentChange}
                    onDriverToggle={boardFilterControls.onDriverToggle}
                    selectedDrivers={boardFilterControls.selectedDrivers}
                    onDriverLocationSaved={patchDriverLocation}
                    // Keyed by the board being shown, like every other control in this bar. Unreachable on My
                    // Biddings today (no picker is rendered), but the two-way key it used to carry would have
                    // cleared the LANE tab's drivers from there if it ever became reachable.
                    onClearDrivers={() => setDriversByTab((prev) => ({ ...prev, [boardStateKey(pinnedBoard, activeTabId)]: EMPTY_DRIVERS }))}
                    hideLaneFilters={bidLoadsOnly}
                    onRefresh={onManualRefresh}
                    canRefresh={bidLoadsOnly || activeTab.searched}
                    mobileMoreFiltersOpen={mobileMoreFiltersOpen}
                    onMobileMoreFiltersToggle={() => setMobileMoreFiltersOpen((open) => !open)}
                    refinementsSlot={
                        <>
                            {companyFilterReady && !bidLoadsOnly && (
                                <BoxTruckCompanyFilter value={companyFilter} onChange={setCompanyFilter} companies={boardCompanies} />
                            )}
                            <BoxTruckRefinementBar values={refinements} onChange={patchRefinements} />
                            {!bidLoadsOnly && (
                                <BoxTruckCapacityControls
                                    equipmentLabels={equipmentLabels}
                                    limits={capacityLimits}
                                    includeUnknown={includeUnknownCapacity}
                                    onLimitsChange={(limits, interaction) => {
                                        trackBoxTruckCapacityLimitChanged(interaction)
                                        updateCapacityFilters(limits, includeUnknownCapacity)
                                    }}
                                    onIncludeUnknownChange={(include) => {
                                        trackBoxTruckUiInteraction('capacity_unknown_toggled', include ? 'on' : 'off')
                                        updateCapacityFilters(capacityLimits, include)
                                    }}
                                    onReset={() => {
                                        trackBoxTruckUiInteraction('capacity_reset')
                                        // `true` is the default everywhere else — resetting must not quietly
                                        // arm the stricter rule that drops DAT rows stating no weight.
                                        updateCapacityFilters(createDefaultBoxTruckCapacityLimits(), true)
                                    }}
                                />
                            )}
                            <BoxTruckAdvancedFilters
                                refinementDefaults={defaultRefinements}
                                search={{ fullPartial, lengthFt, weightLbs, maxPostedAgeHours }}
                                onSearchChange={(patch) => {
                                    if (patch.fullPartial !== undefined) setFullPartial(patch.fullPartial)
                                    if (patch.lengthFt !== undefined) setLengthFt(patch.lengthFt)
                                    if (patch.weightLbs !== undefined) setWeightLbs(patch.weightLbs)
                                    if (patch.maxPostedAgeHours !== undefined) setMaxPostedAgeHours(patch.maxPostedAgeHours)
                                    commitFilterPatch(patch)
                                }}
                                refinements={refinements}
                                onRefinementsChange={patchRefinements}
                                onReset={resetAdvancedFilters}
                                datEnabled={datEnabled}
                                onDatEnabledChange={onDatEnabledChange}
                                beonEnabled={beonEnabled}
                                onBeonEnabledChange={onBeonEnabledChange}
                                beonConnected={beon.connected}
                                onBeonConnect={beon.openModal}
                                showDatSource={!bidLoadsOnly}
                                showMaxAge={!bidLoadsOnly}
                                equipmentLabels={equipmentLabels}
                                datEquipmentTypes={datEquipmentTypes}
                                onDatEquipmentTypesChange={(codes, interaction) => {
                                    trackBoxTruckDatSubtypesChanged(interaction)
                                    const nextDatEquipmentTypes = sanitizeBoxTruckDatSubtypeCodes(codes)
                                    setDatEquipmentTypes(nextDatEquipmentTypes)
                                    // Committed like a capacity edit, not left in page state: the tab and the
                                    // persisted filters are what a tab switch or a refresh restores from, so
                                    // page state alone loses the edit unless Search happens to follow it.
                                    commitFilterPatch({ datEquipmentTypes: nextDatEquipmentTypes })
                                }}
                            />
                            {hasResettableSecondaryFilters && (
                                <button
                                    type="button"
                                    onClick={resetSecondaryFilters}
                                    className="flex h-8 items-center gap-1.5 rounded-3xl px-3 text-sm font-medium text-foreground-muted transition-colors hover:bg-default hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                >
                                    <RotateCcw className="size-4" /> Reset
                                </button>
                            )}
                        </>
                    }
                    trailingSlot={
                        /* Search Vehicles — atrek-style round icon at the row's right edge; the label lives in
                           the hover tooltip. */
                        <div className="group relative">
                            <button
                                type="button"
                                onClick={openSearchVehicles}
                                aria-label="Search Vehicles"
                                className="flex size-9 items-center justify-center rounded-xl border border-border bg-surface text-accent shadow-field transition-colors hover:bg-accent-soft"
                            >
                                {/* Truck-in-magnifier glyph (atrek's): a small truck composed inside the Search lens. */}
                                <span className="relative">
                                    <Search size={20} strokeWidth={2.2} />
                                    <Truck size={9} strokeWidth={2.4} className="absolute left-[4px] top-[4px]" />
                                </span>
                            </button>
                            {/* Tooltip opens UPWARD — below it would sit under the sticky table header's stacking context. */}
                            <span className="pointer-events-none absolute bottom-full right-0 z-20 mb-1.5 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-medium text-background opacity-0 shadow-overlay transition-opacity group-hover:opacity-100">
                                Search Vehicles
                            </span>
                        </div>
                    }
                    sourceSlot={
                        !bidLoadsOnly && sourceGroups.length > 0 ? (
                            <BoxTruckSourceBar groups={sourceGroups} hidden={hiddenSources} onToggle={onToggleSource} />
                        ) : undefined
                    }
                />
            </div>

            <TableSettings
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                title="Box Truck Table Settings"
                tabs={[
                    {
                        id: 'load-detail',
                        label: 'Load detail',
                        content: (
                            <BoxTruckLoadDetailSettings
                                openMapOnExpand={boardPreferences.openMapOnExpand}
                                onOpenMapOnExpandChange={(value) => {
                                    setBoardPreference('openMapOnExpand', value)
                                    trackBoxTruckUiInteraction('open_map_on_expand_toggled', value ? 'on' : 'off')
                                }}
                            />
                        ),
                    },
                ]}
                columns={BOX_TRUCK_COLUMN_DEFS}
                visibleColumns={visibleColumns}
                onColumnChange={toggleColumn}
                columnOrder={columnOrder}
                onColumnOrderChange={reorderColumns}
                minVisibleColumns={1}
                onReset={resetColumns}
            />

            <BoxTruckBoard
                searched={activeTab.searched}
                pinnedBoard={pinnedBoard}
                pinnedFeedError={activeFeed?.error ?? false}
                onReloadPinnedFeed={refresh}
                // Pinned boards only: the lane feed's own fetch-error handling is unchanged.
                failedFetch={activeFeed?.failedFetch ?? null}
                onRetryLoadMore={activeFeed ? activeFeed.loadMore : undefined}
                onRetryReload={activeFeed ? activeFeed.reload : undefined}
                error={error}
                onRetry={runSearch}
                boardLoading={boardLoading}
                boardLoadsCount={boardLoads.length}
                visibleLoads={visibleLoads}
                loadingMore={boardLoadingMore}
                selectedKey={selectedKey}
                onRowClick={handleRowClick}
                onOpenNewTab={handleOpenInNewTab}
                bids={boardBids}
                operatorBids={boardOperatorBids}
                annotations={annotations}
                emailThreads={boardEmailThreads}
                awaitingDesk={boardAwaitingDesk}
                onRate={handleRate}
                currentOperatorId={user?.id}
                onEndReached={boardLoadMore}
                scrollKey={boardStateKey(pinnedBoard, boardTabId)}
                readScrollTop={readBoardScrollTop}
                writeScrollTop={writeBoardScrollTop}
                visibleColumns={visibleColumns}
                columnOrder={columnOrder}
                settingsSlot={
                    <button
                        type="button"
                        onClick={() => setSettingsOpen(true)}
                        // Tinted when columns are hidden, which the Columns button used to say on its own.
                        // Dropping that with the button would have left a board missing columns looking
                        // exactly like one showing all of them.
                        title={hiddenColumnCount > 0 ? `Box Truck table settings — ${hiddenColumnCount} ${columnWord} hidden` : 'Box Truck table settings'}
                        aria-label="Box Truck table settings"
                        className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                            hiddenColumnCount > 0 ? 'text-accent hover:bg-accent-soft' : 'text-foreground-muted hover:bg-default hover:text-foreground'
                        }`}
                    >
                        <Settings size={18} />
                    </button>
                }
            />

            {searchVehiclesMounted && (
                <SearchVehiclesDrawer open={searchVehiclesOpen} onClose={() => setSearchVehiclesOpen(false)} onDriverLocationSaved={patchDriverLocation} />
            )}

            <BoxTruckLoadDrawer
                load={selectedLoad}
                stale={selectedLoadStale}
                openMapOnExpand={boardPreferences.openMapOnExpand}
                // The tab's picked drivers — the operator opened this load FOR them, so Nearby Drivers pins
                // them to the top, synthesizing their rows when the fetch can't supply them (Driver Bids has
                // no lane/driver pick).
                pinnedDrivers={boardFilterControls.selectedDrivers}
                // A pinned row IS the snapshot above, so a correction made from the drawer has to reach the
                // store too — otherwise the next radius refetch rebuilds the row from the pre-edit fix.
                onDriverLocationSaved={patchDriverLocation}
                topOffset={drawerTopOffset}
                onClose={() => setSelectedKey(null)}
                bids={selectedLoad ? (boardBids[loadKey(selectedLoad)] ?? []) : []}
                operatorBids={selectedLoad ? (boardOperatorBids[loadKey(selectedLoad)] ?? []) : []}
                annotation={selectedLoad ? (annotations[loadKey(selectedLoad)] ?? null) : null}
                onSetAnnotation={setAnnotation}
                currentOperatorId={user?.id}
                emailThread={selectedLoad ? (boardEmailThreads[loadKey(selectedLoad)] ?? null) : null}
            />

            {/* Load Hub's connect flow, reused whole, with the credential half suppressed: on this board BEON
                is connected one way — log in at beoncarrier.gobeon.com and let the extension hand the session
                over. Mounted only while open so its 1.5s status poll exists only during the flow. */}
            {beon.modalOpen && (
                <AihubAuthModal
                    open
                    loadboard="beon"
                    extensionOnly
                    onClose={beon.closeModal}
                    onSuccess={() => {
                        beon.closeModal()
                        beon.markConnected()
                        // Connecting is the operator asking for the feed, so switch it on rather than leaving
                        // them a second control to find. Goes through the handler so the gated-source
                        // bookkeeping (unhide, reveal, drop other snapshots) runs exactly as a manual flip.
                        // `justConnected` states what this path knows, so the feed is asked for whether or not
                        // `markConnected` above has landed — the order of these two lines cannot break it.
                        onBeonEnabledChange(true, true)
                    }}
                    checkStatus={beon.checkStatus}
                />
            )}
        </div>
    )
}
